import React, { useState, useEffect, useRef } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaShoppingBag,
  FaSearch,
  FaShieldAlt,
  FaHeart,
  FaUser,
  FaTimes,
  FaBars,
  FaTruck,
  FaHome,
  FaLayerGroup,
  FaChevronRight,
  FaChevronDown
} from "react-icons/fa";
import { getWishlist, getWishlistCount, retainVisibleWishlist } from "../../util/wishlistService";
import { getRecentlyViewed, retainVisibleRecentlyViewed } from "../../util/recentViewedService";
import { imageurl } from "../../App";
import { brandLogoForSurface } from "../../assets/img/logo/brandLogos";
import { loadStoreSettings } from "../../util/storeSettings";
import { loadCategories } from "../../util/storeCatalog";
import {
  getGuestCart,
  getGuestCartCount,
  retainVisibleCartProducts,
  openCartDrawer,
  CART_UPDATED_EVENT,
  setStoreFreeShippingThreshold
} from "../../util/guestCartService";
import { readCachedStoreSettings } from "../../util/storeLogoHelper";
import { clearCustomerSession, customerName, customerRole, isCustomerLoggedIn } from "../../util/customerAuth";
import SearchModal from "../../components/frontend/SearchModal";
import {
  isExternalLink,
  menuBranchIsActive,
  menuChildren,
  normalizeMenuLink,
  visibleHeaderMenu,
} from "../../themes/headerMenu";

function StoreLink({ to, className, children, onClick }) {
  const href = normalizeMenuLink(to);
  if (!href) {
    return <span className={className}>{children}</span>;
  }
  if (isExternalLink(href)) {
    return (
      <a href={href} className={className} onClick={onClick} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

function MobileMenuItems({ items, pathname, openMenuIds, onToggle, onNavigate }) {
  return items.map((entry) => {
    const children = menuChildren(entry);
    const dropdown = children.length > 0;
    const active = menuBranchIsActive(entry, pathname);
    return (
      <li key={entry.id}>
        <div className="mobile-nav-row">
          <StoreLink
            to={entry.link}
            className={`mobile-nav-item ${active ? "active" : ""}`}
            onClick={onNavigate}
          >
            <span className="mobile-nav-text">{entry.label}</span>
            {!dropdown && <FaChevronRight className="mobile-nav-arrow" />}
          </StoreLink>
          {dropdown && (
            <button
              type="button"
              className="mobile-nav-expand"
              aria-label={`${openMenuIds[entry.id] ? "Hide" : "Show"} ${entry.label}`}
              onClick={() => onToggle(entry.id)}
            >
              <FaChevronDown
                size={12}
                style={{ transform: openMenuIds[entry.id] ? "rotate(180deg)" : undefined }}
              />
            </button>
          )}
        </div>
        {dropdown && openMenuIds[entry.id] && (
          <ul className="mobile-submenu">
            <MobileMenuItems
              items={children}
              pathname={pathname}
              openMenuIds={openMenuIds}
              onToggle={onToggle}
              onNavigate={onNavigate}
            />
          </ul>
        )}
      </li>
    );
  });
}

function DesktopMenu({ items, pathname, nested = false, className }) {
  return (
    <ul className={nested ? "nav-submenu" : className || "nav-links-modern d-none d-md-flex"}>
      {items.map((entry) => {
        const children = menuChildren(entry);
        const dropdown = children.length > 0;
        const active = menuBranchIsActive(entry, pathname);
        return (
          <li key={entry.id} className={dropdown ? "nav-menu-item" : undefined}>
            <StoreLink
              to={entry.link}
              className={`${nested ? "nav-submenu-link" : "nav-link-modern"} ${active ? "active" : ""}`}
            >
              <span>{entry.label}</span>
              {dropdown && (nested ? <FaChevronRight size={10} /> : <FaChevronDown size={10} />)}
            </StoreLink>
            {dropdown && <DesktopMenu items={children} pathname={pathname} nested />}
          </li>
        );
      })}
    </ul>
  );
}

function BrandMark({ storeSettings, stacked, logoOnly = false, surface = "#ffffff" }) {
  if (!storeSettings) {
    return (
      <Link
        className={stacked ? "header-wordmark header-brand-pending" : "navbar-brand-modern header-brand-pending"}
        to="/"
        aria-hidden="true"
      />
    );
  }

  const name = storeSettings?.storeName || "Store";
  const logo = storeSettings?.logoUrl
    ? storeSettings.logoUrl.startsWith("http")
      ? storeSettings.logoUrl
      : `${imageurl}/${storeSettings.logoUrl}`
    : "";

  if (!stacked) {
    return (
      <Link className="navbar-brand-modern" to="/">
        <img
          src={logo || brandLogoForSurface(surface)}
          alt={name}
        />
      </Link>
    );
  }

  return (
    <Link className="header-wordmark" to="/">
      {logo ? (
        <img src={logo} alt={name} />
      ) : (
        <img
          src={brandLogoForSurface(surface)}
          alt={name}
        />
      )}
      {logoOnly ? null : <span className="header-store-name">{name}</span>}
    </Link>
  );
}

function AccountMenu() {
  const history = useHistory();
  const loggedIn = isCustomerLoggedIn();
  const [open, setOpen] = useState(false);

  if (!loggedIn) {
    return (
      <Link className="nav-action-btn" to="/login" aria-label="Sign in">
        <FaUser size={14} />
      </Link>
    );
  }

  const signOut = () => {
    setOpen(false);
    axios.post("/api/logout").catch(() => {}).finally(() => {
      clearCustomerSession();
      history.push("/");
    });
  };

  return (
    <div className="position-relative">
      <button
        type="button"
        className="nav-action-btn border-0"
        aria-label="Account"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <FaUser size={14} />
      </button>
      {open && (
        <div
          className="account-menu"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 8px)",
            zIndex: 40,
            minWidth: "190px",
            background: "#ffffff",
            color: "#111111",
            borderRadius: "10px",
            boxShadow: "0 10px 28px rgba(0,0,0,0.14)",
            padding: "8px 0",
          }}
        >
          <div style={{ padding: "8px 14px 6px", fontSize: "13px", fontWeight: 700 }}>
            {customerName() || "Account"}
          </div>
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            style={{ display: "block", padding: "8px 14px", color: "#111111", textDecoration: "none", fontSize: "14px" }}
          >
            Orders and account
          </Link>
          <button
            type="button"
            onClick={signOut}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              padding: "8px 14px",
              border: 0,
              background: "transparent",
              fontSize: "14px",
            }}
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

function HeaderTools({ searchOpen, onSearch, cartCount }) {
  return (
    <div className="header-centered-tools">
      <button
        type="button"
        className={`nav-action-btn border-0 ${searchOpen ? "active" : ""}`}
        onClick={onSearch}
        aria-label="Search products"
      >
        <FaSearch size={15} />
      </button>
      <AccountMenu />
      <button
        type="button"
        className="nav-action-btn border-0"
        onClick={openCartDrawer}
        aria-label="Open cart"
      >
        <FaShoppingBag size={15} />
        {cartCount > 0 && <span className="cart-badge-count">{cartCount}</span>}
      </button>
    </div>
  );
}

function headerColors(settings, layout) {
  if (layout === "logo-center" || layout === "prestige") {
    return {
      background: settings?.headerBackground || "#ffffff",
      color: settings?.headerTextColor || "#111827",
    };
  }
  return {
    background: settings?.headerBackground || "#0a0a0a",
    color: settings?.headerTextColor || "#f7f7f7",
  };
}

export default function Navbar(props) {
  const headerLayout = props.themeSettings?.headerLayout || "classic";
  const headerSurface = headerColors(props.themeSettings, headerLayout).background;
  const headerMenu = visibleHeaderMenu(props.themeSettings);
  const [openMenuIds, setOpenMenuIds] = useState({});
  const [categoryList, setCategoryList] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(getWishlistCount());
  const [cartCount, setCartCount] = useState(getGuestCartCount());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [storeSettings, setStoreSettings] = useState(() => {
    if (props.storeSettingsOverride) return props.storeSettingsOverride;
    const primed = window.__ECOM_STORE_SETTINGS__;
    if (primed && (primed.storeName || primed.logoUrl)) return primed;
    const saved = readCachedStoreSettings();
    return saved && (saved.storeName || saved.logoUrl) ? saved : null;
  });
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();
  const headerRef = useRef(null);

  const isAdmin = customerRole() === "admin" || customerRole() === "superadmin";

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const apply = () => {
      document.documentElement.style.setProperty("--header-offset", `${el.offsetHeight}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    let ismounted = true;
    if (ismounted) {
      loadCategories().then((categories) => {
        if (ismounted) setCategoryList(categories || []);
      }).catch(() => {});

      const storedIds = []
        .concat(getGuestCart().map((item) => item.product_id || item.id))
        .concat(getWishlist().map((item) => item.id))
        .concat(getRecentlyViewed().map((item) => item.id))
        .map((id) => String(id).split(":")[0])
        .filter((id) => id && id !== "undefined");
      const uniqueIds = Array.from(new Set(storedIds));
      if (uniqueIds.length) {
        axios.get(`/api/storefront-visible-products?ids=${uniqueIds.join(",")}`).then((res) => {
          if (res.data.status === 200 && Array.isArray(res.data.ids)) {
            retainVisibleCartProducts(res.data.ids);
            retainVisibleWishlist(res.data.ids);
            retainVisibleRecentlyViewed(res.data.ids);
          }
        }).catch(() => {});
      }

      if (!props.storeSettingsOverride) {
        loadStoreSettings().then((settings) => {
          if (!settings || (!settings.storeName && !settings.logoUrl)) return;
          setStoreSettings(settings);
          if (settings.freeShippingThreshold !== undefined) {
            setStoreFreeShippingThreshold(settings.freeShippingThreshold);
          }
        }).catch(() => {});
      }
    }

    const updateWishlist = () => {
      setWishlistCount(getWishlistCount());
    };

    const updateCart = () => {
      setCartCount(getGuestCartCount());
    };

    window.addEventListener("wishlistUpdated", updateWishlist);
    window.addEventListener(CART_UPDATED_EVENT, updateCart);

    return () => {
      ismounted = false;
      window.removeEventListener("wishlistUpdated", updateWishlist);
      window.removeEventListener(CART_UPDATED_EVENT, updateCart);
    };
  }, [props.storeSettingsOverride]);

  // Close mobile menu & search dropdown on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile offcanvas drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen || searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen, searchOpen]);

  // Handle header elevation shadow on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);



  return (
    <div className="modern-navbar-wrapper" ref={headerRef}>
      {headerLayout === "prestige" ? (
      <nav
        className={`modern-navbar header-layout-prestige ${scrolled ? "scrolled" : ""}`}
        style={headerColors(props.themeSettings, headerLayout)}
      >
        <div className="header-prestige">
          <div className="header-prestige-side is-left">
            <button
              type="button"
              className="nav-action-btn d-md-none border-0"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <FaBars size={17} />
            </button>
            <DesktopMenu
              items={headerMenu}
              pathname={location.pathname}
              className="header-prestige-menu nav-links-modern d-none d-md-flex"
            />
          </div>
          <Link className="header-prestige-mark" to="/">
            {storeSettings?.storeName || "Store"}
          </Link>
          <div className="header-prestige-side is-right">
            <HeaderTools
              searchOpen={searchOpen}
              onSearch={() => setSearchOpen(true)}
              cartCount={cartCount}
            />
          </div>
        </div>
      </nav>
      ) : headerLayout === "classic" ? (
      <nav className={`modern-navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="container d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            {/* Mobile Hamburger Toggle Button */}
            <button
              type="button"
              className="nav-action-btn d-md-none border-0"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
              title="Open Navigation Menu"
            >
              <FaBars size={17} />
            </button>

            {/* Logo */}
            <Link className="navbar-brand-modern" to="/">
              {storeSettings?.logoUrl ? (
                <img
                  src={storeSettings.logoUrl.startsWith("http") ? storeSettings.logoUrl : `${imageurl}/${storeSettings.logoUrl}`}
                  alt={storeSettings?.storeName || "Store Logo"}
                />
              ) : storeSettings ? (
                <img
                  src={brandLogoForSurface("#ffffff")}
                  alt={storeSettings?.storeName || "Store Logo"}
                />
              ) : (
                <span className="header-brand-pending" />
              )}
            </Link>
          </div>

          {/* Desktop Product Search */}
          <div className="nav-search-container d-none d-lg-block">
            <button type="button" className="nav-search-launch" onClick={() => setSearchOpen(true)}>
              <FaSearch size={13} />
              <span>Search products</span>
            </button>
          </div>

          {/* Desktop Nav Links */}
          <DesktopMenu items={headerMenu} pathname={location.pathname} />

          {/* Actions: Mobile Search, Wishlist & Cart Drawer Trigger */}
          <div className="d-flex align-items-center gap-2">
            {/* Mobile / Tablet Quick Search Trigger */}
            <button
              type="button"
              className={`nav-action-btn d-lg-none border-0 ${searchOpen ? "active" : ""}`}
              onClick={() => setSearchOpen(true)}
              title="Search products"
              aria-label="Search products"
            >
              <FaSearch size={15} />
            </button>

            <AccountMenu />

            {/* Wishlist Link with counter */}
            <Link
              className="nav-action-btn"
              to="/wishlist"
              title="My Wishlist"
              aria-label="My Wishlist"
            >
              <FaHeart size={15} />
              {wishlistCount > 0 && (
                <span
                  className="cart-badge-count"
                  style={{ background: "var(--accent-gold)" }}
                >
                  {wishlistCount}
                </span>
              )}
            </Link>

            {/* Cart Drawer Trigger Button */}
            <button
              type="button"
              className="nav-action-btn border-0"
              onClick={openCartDrawer}
              title="Open Cart Drawer"
              aria-label="Open Cart Drawer"
            >
              <FaShoppingBag size={15} />
              {cartCount > 0 && (
                <span className="cart-badge-count">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

      </nav>
      ) : (
      <nav
        className={`modern-navbar header-layout-${headerLayout} ${scrolled ? "scrolled" : ""}`}
        style={headerColors(props.themeSettings, headerLayout)}
      >
        <div className={headerLayout === "logo-center" ? "header-logo-center" : "header-centered-bar"}>
          {headerLayout === "logo-center" ? (
            <>
              <div className="header-logo-row">
                <div className="header-centered-side">
                  <button
                    type="button"
                    className="nav-action-btn d-md-none border-0"
                    onClick={() => setMobileMenuOpen(true)}
                    aria-label="Open navigation menu"
                  >
                    <FaBars size={17} />
                  </button>
                </div>
                <div className="header-logo-slot">
                  <BrandMark storeSettings={storeSettings} stacked surface={headerSurface} />
                </div>
                <HeaderTools
                  searchOpen={searchOpen}
                  onSearch={() => setSearchOpen(true)}
                  cartCount={cartCount}
                />
              </div>
              <DesktopMenu
                items={headerMenu}
                pathname={location.pathname}
                className="header-centered-menu nav-links-modern d-none d-md-flex"
              />
            </>
          ) : (
            <>
              <div className="header-centered-side">
                <button
                  type="button"
                  className="nav-action-btn d-md-none border-0"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Open navigation menu"
                >
                  <FaBars size={17} />
                </button>
                <BrandMark storeSettings={storeSettings} stacked surface={headerSurface} />
              </div>
              <DesktopMenu
                items={headerMenu}
                pathname={location.pathname}
                className="header-centered-menu nav-links-modern d-none d-md-flex"
              />
              <HeaderTools
                searchOpen={searchOpen}
                onSearch={() => setSearchOpen(true)}
                cartCount={cartCount}
              />
            </>
          )}
        </div>
      </nav>
      )}

      {/* Mobile Offcanvas Navigation Drawer */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop Overlay */}
          <div
            className="mobile-nav-backdrop"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu backdrop"
          />

          {/* Offcanvas Drawer Panel */}
          <div className="mobile-nav-drawer" role="dialog" aria-modal="true" aria-label="Mobile Navigation">
            {/* Drawer Header */}
            <div className="mobile-drawer-header">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                {storeSettings?.logoUrl ? (
                  <img
                    src={storeSettings.logoUrl.startsWith("http") ? storeSettings.logoUrl : `${imageurl}/${storeSettings.logoUrl}`}
                    alt={storeSettings?.storeName || "Store Logo"}
                  />
                ) : storeSettings ? (
                  <img
                    src={brandLogoForSurface("#ffffff")}
                    alt={storeSettings?.storeName || "Store Logo"}
                  />
                ) : (
                  <span className="header-brand-pending" />
                )}
              </Link>
              <button
                type="button"
                className="mobile-drawer-close"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <FaTimes size={15} />
              </button>
            </div>

            {/* In-drawer Quick Search */}
            <div className="mobile-drawer-search-wrap">
              <button
                type="button"
                className="nav-search-launch"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setSearchOpen(true);
                }}
              >
                <FaSearch size={12} />
                <span>Search products</span>
              </button>
            </div>

            {/* Drawer Body - Navigation Links */}
            <div className="mobile-drawer-body">
              <ul className="mobile-nav-list">
                <MobileMenuItems
                  items={headerMenu}
                  pathname={location.pathname}
                  openMenuIds={openMenuIds}
                  onToggle={(id) =>
                    setOpenMenuIds((current) => ({ ...current, [id]: !current[id] }))
                  }
                  onNavigate={() => setMobileMenuOpen(false)}
                />
                <li>
                  <Link
                    to={isCustomerLoggedIn() ? "/account" : "/login"}
                    className={`mobile-nav-item ${location.pathname === "/account" || location.pathname === "/login" ? "active" : ""}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="mobile-nav-icon"><FaUser /></span>
                    <span className="mobile-nav-text">{isCustomerLoggedIn() ? (customerName() || "Account") : "Sign in"}</span>
                    <FaChevronRight className="mobile-nav-arrow" />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/wishlist"
                    className={`mobile-nav-item ${location.pathname === "/wishlist" ? "active" : ""}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="mobile-nav-icon"><FaHeart /></span>
                    <span className="mobile-nav-text">My Wishlist</span>
                    {wishlistCount > 0 && <span className="mobile-nav-badge">{wishlistCount}</span>}
                    <FaChevronRight className="mobile-nav-arrow" />
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    className="mobile-nav-item text-start border-0 bg-transparent"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      openCartDrawer();
                    }}
                  >
                    <span className="mobile-nav-icon"><FaShoppingBag /></span>
                    <span className="mobile-nav-text">Shopping Cart</span>
                    {cartCount > 0 && <span className="mobile-nav-badge badge-cart">{cartCount}</span>}
                    <FaChevronRight className="mobile-nav-arrow" />
                  </button>
                </li>
                {isAdmin && (
                  <li>
                    <Link
                      to="/admin"
                      className="mobile-nav-item mobile-nav-admin"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="mobile-nav-icon text-danger"><FaShieldAlt /></span>
                      <span className="mobile-nav-text fw-bold text-danger">Admin Dashboard</span>
                      <FaChevronRight className="mobile-nav-arrow" />
                    </Link>
                  </li>
                )}
              </ul>

              {/* Dynamic Category Chips */}
              {categoryList && categoryList.length > 0 && (
                <div className="mobile-drawer-categories">
                  <div className="mobile-drawer-section-title">Shop by Category</div>
                  <div className="mobile-category-chips">
                    {categoryList.slice(0, 10).map((cat) => (
                      <Link
                        key={cat.id || cat.slug}
                        to={`/collection/${cat.slug}`}
                        className="mobile-category-chip"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            <div className="mobile-drawer-footer">
              <div className="d-flex align-items-center gap-2 text-muted">
                <FaTruck size={14} className="text-primary flex-shrink-0" />
                <span>Free Express Delivery Across {storeSettings?.country || "Pakistan"}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile App-like Bottom Navigation Dock */}
      <nav className="mobile-bottom-nav d-md-none" aria-label="Mobile Bottom Navigation">
        <Link
          to="/"
          className={`mobile-bottom-item ${location.pathname === "/" ? "active" : ""}`}
        >
          <FaHome size={19} />
          <span>Home</span>
        </Link>
        <Link
          to="/collection"
          className={`mobile-bottom-item ${location.pathname.startsWith("/collection") ? "active" : ""}`}
        >
          <FaLayerGroup size={19} />
          <span>Categories</span>
        </Link>
        <button
          type="button"
          className={`mobile-bottom-item border-0 ${searchOpen ? "active" : ""}`}
          onClick={() => setSearchOpen(true)}
          aria-label="Search"
        >
          <FaSearch size={18} />
          <span>Search</span>
        </button>
        <Link
          to="/wishlist"
          className={`mobile-bottom-item ${location.pathname === "/wishlist" ? "active" : ""}`}
        >
          <div className="position-relative d-inline-flex">
            <FaHeart size={19} />
            {wishlistCount > 0 && (
              <span className="mobile-bottom-badge">{wishlistCount}</span>
            )}
          </div>
          <span>Wishlist</span>
        </Link>
        <button
          type="button"
          className="mobile-bottom-item border-0"
          onClick={openCartDrawer}
          aria-label="Open Cart"
        >
          <div className="position-relative d-inline-flex">
            <FaShoppingBag size={19} />
            {cartCount > 0 && (
              <span className="mobile-bottom-badge badge-cart">{cartCount}</span>
            )}
          </div>
          <span>Cart</span>
        </button>
      </nav>
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} settings={props.themeSettings} />
    </div>
  );
}
